import React, { useState, useEffect, useContext } from 'react'
import { Button, Card, Modal } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import moment from 'moment'
import DefaultVoucher from "../../assets/default-voucher.png"
import DefaultProduct from "../../assets/default-product.jpg"
import { QRCodeSVG } from 'qrcode.react'
import { ToastContainer, toast } from 'react-toastify'
import { AiOutlineCalendar, AiOutlineShop, AiOutlineQrcode, AiOutlineEye, AiOutlineGift } from "react-icons/ai"
import { formatVoucherDiscount } from '../../utils/voucherDiscount'
import { ThemeContext } from '../../context/ThemeContext'

const lightenHexColor = (color, amount = 0.78) => {
    const normalized = `${color || ''}`.trim().replace('#', '')
    const hex = normalized.length === 3
        ? normalized.split('').map((character) => `${character}${character}`).join('')
        : normalized

    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return '#f6ddda'

    const channel = (offset) => Number.parseInt(hex.slice(offset, offset + 2), 16)
    const lightened = [channel(0), channel(2), channel(4)]
        .map((value) => Math.round(value + ((255 - value) * amount)))
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('')

    return `#${lightened}`
}

function Redeem() {
	const { primaryColor } = useContext(ThemeContext)
    const [voucher, setVoucher] = useState([])
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [selectedVoucher, setSelectedVoucher] = useState(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [showQRModal, setShowQRModal] = useState(false)
    const [qrcode, setQrcode] = useState()
    const [category, setCategory] = useState('marketplace')
    const voucherPrimaryColor = primaryColor || '#a34b3f'
    const voucherPrimaryLightColor = lightenHexColor(voucherPrimaryColor)
    const categoryButtonStyle = (isActive) => ({
        backgroundColor: isActive ? voucherPrimaryColor : voucherPrimaryLightColor,
        color: isActive ? '#ffffff' : voucherPrimaryColor,
    })

    useEffect(() => {
        init(1, false)
    }, [category])

    const init = async (targetPage = 1, append = false) => {
        if (append) setLoadingMore(true)
        else setLoading(true)
        const params = {
            access: 'auth',
            method: API.REDEEM_VOUCHER.type,
            url: API.REDEEM_VOUCHER.url,
            reqBody: {
                page: targetPage,
                rows: 5,
                category
            }
        }

        const response = await Api(params);
        if (response.status) {
            const nextRows = response.data.data || []
            setVoucher((current) => append ? [...current, ...nextRows] : nextRows)
            setLastPage(response.data.lastPage)
            setPage(response.data.page)
        }
        setLoading(false)
        setLoadingMore(false)
    }

    const loadMore = () => {
        if (loadingMore || page >= lastPage) return
        init(page + 1, true)
    }

    const showQRCode = async (v) => {
        if ((v?.voucher_category || v?.voucher?.category) !== 'offline') return
        const response = await Api({
            access: 'auth',
            method: API.OFFLINE_VOUCHER_QR.type,
            url: API.OFFLINE_VOUCHER_QR.url,
            reqBody: { member_voucher_id: v.member_voucher_id }
        })
        if (!response?.status || !response?.data?.qr_token) {
            toast.error(response?.message || 'Gagal membuat QR voucher')
            return
        }
        setQrcode(response.data.qr_token)
        setSelectedVoucher(v)
        setShowQRModal(true)
    }

    const showVoucher = (v) => {
        setSelectedVoucher(v)
        setShowDetailModal(true)
    }

    const handleCloseModal = () => {
        setShowDetailModal(false)
        setShowQRModal(false)
        setSelectedVoucher(null)
        setQrcode(null)
    }

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-4">
            <div className="bg-gray-200 h-8 rounded w-1/3 mx-auto"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-80 rounded-lg"></div>
                ))}
            </div>
        </div>
    )
    // Keep the current vouchers visible while the newly selected category loads.
    // The full-page skeleton is reserved for the first load only.
    if (loading && voucher.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <LoadingSkeleton />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <ToastContainer autoClose={1000} />
            <div className="mx-auto max-w-sm px-5 py-6">
                <div className='flex flex-col space-y-6 scrollbar-hide'>

                    {/* Header Section */}
                    <div className="text-center space-y-2">
                        <div className="flex items-center justify-center space-x-2">
                            <AiOutlineGift className="text-2xl text-purple-600" />
                            <h1 className="text-2xl font-bold text-gray-800">Voucher Saya</h1>
                        </div>
                        <p className="text-gray-600 text-sm">Kelola voucher Anda dengan mudah</p>
                        <div className="mt-4 flex gap-2">
                            <Button size="sm" className="flex-1 rounded-full border-none hover:brightness-95" style={categoryButtonStyle(category === 'marketplace')} onClick={() => { if (category !== 'marketplace') { setPage(1); setCategory('marketplace') } }}>Marketplace</Button>
                            <Button size="sm" className="flex-1 rounded-full border-none hover:brightness-95" style={categoryButtonStyle(category === 'offline')} onClick={() => { if (category !== 'offline') { setPage(1); setCategory('offline') } }}>Toko</Button>
                        </div>
                        <p className="text-xs text-slate-500">{category === 'offline' ? 'Tunjukkan QR kepada petugas toko untuk menukar voucher.' : 'Voucher ini hanya dapat digunakan saat checkout di aplikasi.'}</p>
                    </div>

                    {/* Voucher Grid */}
                    {voucher.length === 0 ? (
                        <Card className="bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-300">
                            <Card.Body className="text-center py-12">
                                <AiOutlineGift className="text-6xl text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium text-lg">Belum ada voucher</p>
                                <p className="text-gray-400 text-sm mt-1">Kumpulkan poin untuk mendapatkan voucher menarik</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {voucher.map((v, i) => (
                                <Card key={i} className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 border-0 overflow-hidden">
                                    <Card.Body
                                        className="gap-3 p-3"
                                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                                    >
                                        {/* Voucher Image */}
                                        <div className="relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50">
                                            <img
                                                className="h-full w-full object-cover"
                                                src={v?.voucher?.product_image ? v?.voucher?.product_image : DefaultVoucher}
                                                alt="Voucher"
                                                onError={(e) => {
                                                    e.target.src = DefaultVoucher
                                                }}
                                            />
                                            <div className="absolute right-1 top-1">
                                                <div className="rounded-full bg-green-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                                    Aktif
                                                </div>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="min-w-0 flex-1 space-y-2">
                                            <div>
                                                <h3 className="font-bold text-lg text-gray-800 leading-tight">
                                                    {v?.voucher?.name}
                                                </h3>
                                                <div className="flex items-center space-x-1 mt-1">
                                                    <AiOutlineShop className="text-gray-500 text-sm" />
                                                    <span className="text-gray-600 text-sm">{v?.voucher?.partner?.name}</span>
                                                </div>
                                            </div>

                                            <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                                                {v?.voucher?.description}
                                            </p>

                                            <div className="inline-flex rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
                                                {formatVoucherDiscount(v)}
                                            </div>

                                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                                <div className="flex items-center space-x-1">
                                                    <AiOutlineCalendar className="text-gray-500 text-sm" />
                                                    <span className="text-gray-600 text-sm">
                                                        {moment(v?.expire_date).format('DD/MM/YY')}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                    Exp: {moment(v?.expire_date).fromNow()}
                                                </span>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex space-x-2 pt-2">
                                                <Button
                                                    size="sm"
                                                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white border-none"
                                                    onClick={() => showVoucher(v)}
                                                >
                                                    <AiOutlineEye className="mr-1" />
                                                    Lihat
                                                </Button>
                                                {(v?.voucher_category || v?.voucher?.category) === 'offline' ? (
                                                    <Button size="sm" className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-none" onClick={() => showQRCode(v)}><AiOutlineQrcode className="mr-1" />Tukar</Button>
                                                ) : (
                                                    <div className="flex-1 rounded-md bg-amber-50 px-2 py-2 text-center text-xs font-medium text-amber-800">Gunakan saat checkout</div>
                                                )}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}

                    {voucher.length > 0 && page < lastPage && (
                        <div className="pt-2 text-center"><Button size="sm" className="rounded-full border border-slate-200 bg-white text-slate-700" loading={loadingMore} disabled={loadingMore} onClick={loadMore}>Tampilkan lebih banyak</Button></div>
                    )}
                </div>
            </div>

            {/* Voucher Detail Modal */}
            <Modal.Legacy open={showDetailModal} onClickBackdrop={handleCloseModal}>
                <Modal.Header className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                    <div className="flex items-center space-x-2">
                        <AiOutlineEye className="text-xl" />
                        <span className="font-bold text-lg">Detail Voucher</span>
                    </div>
                </Modal.Header>

                <Modal.Body className="space-y-4 max-h-96 overflow-y-auto">
                    {selectedVoucher && (
                        <>
                            {/* Voucher Image */}
                            <div className="flex justify-center">
                                <img
                                    className='w-full max-w-sm rounded-lg shadow-md'
                                    src={selectedVoucher?.voucher?.product_image ? selectedVoucher?.voucher?.product_image : DefaultProduct}
                                    alt="Voucher"
                                    onError={(e) => {
                                        e.target.src = DefaultProduct
                                    }}
                                />
                            </div>

                            {/* Voucher Info */}
                            <div className="space-y-3">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <h3 className="font-bold text-lg text-gray-800">{selectedVoucher?.voucher?.name}</h3>
                                    <p className="text-gray-600 text-sm">{selectedVoucher?.voucher?.sku}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex items-center space-x-2">
                                        <AiOutlineCalendar className="text-blue-500" />
                                        <div>
                                            <p className="text-xs text-gray-500">Berlaku sampai</p>
                                            <p className="font-semibold text-gray-800">
                                                {selectedVoucher?.expire_date ? moment(selectedVoucher.expire_date).format('DD/MM/YY HH:mm') : '-'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <AiOutlineShop className="text-green-500" />
                                        <div>
                                            <p className="text-xs text-gray-500">Penyedia</p>
                                            <p className="font-semibold text-gray-800">{selectedVoucher?.voucher?.partner?.name}</p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Deskripsi</h4>
                                    <p className="text-gray-600 text-sm leading-relaxed">{selectedVoucher?.voucher?.description}</p>
                                </div>

                                <div className="bg-red-50 p-3 rounded-lg">
                                    <p className="text-xs text-red-500">Nilai voucher</p>
                                    <p className="font-semibold text-red-700">{formatVoucherDiscount(selectedVoucher)}</p>
                                </div>

                                {/* Redeem Locations */}
                                {selectedVoucher?.voucher?.partner?.redeem_merchants && selectedVoucher.voucher.partner.redeem_merchants.length > 0 && (
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-2">Lokasi Penukaran</h4>
                                        <div className="space-y-2">
                                            {selectedVoucher.voucher.partner.redeem_merchants.map((location, i) => (
                                                <div key={i} className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-500">
                                                    <p className="font-medium text-gray-800">{location.name}</p>
                                                    <p className="text-sm text-gray-600">{location.address}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Actions className="justify-end">
                    <Button variant="outline" onClick={handleCloseModal}>
                        Tutup
                    </Button>
                </Modal.Actions>
            </Modal.Legacy>

            {/* QR Code Modal */}
            <Modal.Legacy open={showQRModal} onClickBackdrop={handleCloseModal} className="mx-4 max-w-sm self-center justify-self-center">
                <Modal.Header className="border-b border-rose-100 bg-gradient-to-r from-[#9f4d42] to-[#c36a59] p-0 text-white">
                    <div className="flex items-center gap-2.5">
                        <div>
                            <span className="text-base font-bold">Tukar Voucher</span>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="space-y-3 bg-[#fffaf8] p-0 text-center">
                    {selectedVoucher && (
                        <>
                            <div>
                                <h3 className="text-sm font-bold text-slate-800">Siap ditukarkan</h3>
                                <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                                    Tunjukkan kode QR ini kepada petugas toko untuk menggunakan voucher Anda.
                                </p>
                            </div>

                            <div className="rounded-2xl border border-rose-100 bg-white p-2.5 shadow-[0_6px_18px_rgba(159,77,66,0.10)]">
                                <div className="flex justify-center rounded-xl bg-[#fff8f5] p-2">
                                    <QRCodeSVG
                                        value={qrcode || ''}
                                        size={180}
                                        bgColor="#fff8f5"
                                        fgColor="#34211e"
                                        level="H"
                                        includeMargin
                                    />
                                </div>
                                <p className="mt-2 text-[11px] font-medium text-slate-500">Scan hanya saat Anda berada di kasir</p>
                            </div>

                            <div className="rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-left">
                                <p className="truncate text-sm font-bold text-slate-800">{selectedVoucher?.voucher?.name}</p>
                                <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
                                    <span className="font-medium text-[#a34b3f]">Voucher aktif</span>
                                    <span className="text-slate-500">Berlaku s.d. {moment(selectedVoucher?.expire_date).format('DD MMM YYYY')}</span>
                                </div>
                            </div>

                            <p className="text-[11px] leading-relaxed text-amber-800">
                                Jangan bagikan QR ini kepada orang lain sebelum voucher digunakan.
                            </p>
                        </>
                    )}
                </Modal.Body>

                <Modal.Actions className="border-t border-rose-100 bg-white p-0">
                    <Button className="w-full border-none bg-[#a34b3f] text-white hover:bg-[#873c32]" onClick={handleCloseModal}>
                        Tutup
                    </Button>
                </Modal.Actions>
            </Modal.Legacy>

        </div>
    )
}

export default Redeem
